import { SafetyCertificateFilled } from "@ant-design/icons";
import type { AIConnector } from "@p0security/shared/types/ai-inventory";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Tooltip } from "antd";
import { memo } from "react";
import { color, font, numericStyle, radius } from "theme/aiInventory";

import type { ManagedMcpServer } from "../../hooks/useManagedMcpServers.ts";
import { LiveDot } from "./LiveDot.tsx";
import { connectorIconFor } from "./connectorIcon.tsx";

/**
 * A managed MCP server — the authoritative view of an integration the
 * P0 Gateway brokers traffic to. Source: `o/:tenantId/integrations/mcp-server`.
 *
 * In the per-agent graph we only render servers that the agent actually
 * uses (matched to one of its connectors). The full registry list lives
 * in the Gateway popover instead, so the graph stays focused on this
 * agent's dependencies without duplicating the connector it already shows.
 */
export type MCPServerNodeData = {
  server: ManagedMcpServer;
  /** The user-facing connector this server node stands in for, if any. */
  connector: AIConnector | undefined;
  /** Count of tools exposed by the matched connector. */
  toolCount: number;
  /** Whether the server is currently serving this agent. */
  live: boolean;
  expanded: boolean;
};

const containerFor = (live: boolean): React.CSSProperties => ({
  background: live ? color.integrationBg : color.card,
  border: live
    ? `1px solid ${color.integration}`
    : `1px dashed ${color.ruleStrong}`,
  borderRadius: radius.card,
  padding: "8px 10px 8px 14px",
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  minWidth: 160,
  color: live ? color.ink : color.inkMute,
  textAlign: "left",
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: live ? "pointer" : "default",
});

const kicker: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: color.integration,
  marginBottom: 1,
};

const typeChip = (type: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "0 4px",
  borderRadius: radius.sharp,
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  background:
    type === "p0"
      ? color.controlPlaneBg
      : type === "external"
        ? color.card
        : color.mute,
  color:
    type === "p0"
      ? color.controlPlane
      : type === "external"
        ? color.inkDim
        : color.inkDim,
});

const countPill: React.CSSProperties = {
  ...numericStyle,
  marginLeft: "auto",
  padding: "2px 6px",
  borderRadius: radius.sharp,
  fontSize: 10,
  background: color.integrationBgDeep,
  color: color.inkDim,
};

const iconBadge: React.CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: color.integrationBgDeep,
  color: color.integration,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  flexShrink: 0,
};

export const MCPServerNode: React.FC<NodeProps> = memo(({ data }) => {
  const typed = data as MCPServerNodeData;
  const { server, toolCount, live } = typed;
  return (
    <div style={containerFor(live)} data-testid="mcp-server-node">
      <Handle type="target" position={Position.Left} />
      {live && <LiveDot label="Serving live traffic" />}
      <span style={iconBadge} aria-hidden>
        {connectorIconFor(server.label)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={kicker}>
          {live ? (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Tooltip title="Brokered by the P0 Gateway — sessions, tools, and policies enforced">
                <SafetyCertificateFilled
                  aria-label="Gateway-secured"
                  style={{ fontSize: 10, color: color.integration }}
                />
              </Tooltip>
              MCP server · secured
            </span>
          ) : (
            "MCP server · idle"
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {server.label}
          </span>
          <span style={typeChip(server.type)}>{server.type}</span>
        </div>
      </div>
      {live && toolCount > 0 && (
        <span style={countPill}>{toolCount} tools</span>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
});
MCPServerNode.displayName = "MCPServerNode";
